import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { useState } from 'react';

import { Col, Row, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import classnames from 'classnames';

import {
  CustomNotification,
  FormAddButton,
  FormButton,
  FormCancelButton,
  FormSaveButton,
  StyledModal,
} from 'common/components';

import * as api from 'config/axios';
import * as aplEndpoints from 'services/apl/endpoints';

import { saveFile } from 'utils/saveFile';

import { toggleReloadGrid } from 'pages/product-apl-detail/controllers/actions';

import { getAplDetail } from 'pages/product-apl-detail/controllers/actions';

import {
  toggleReloadGridApl,
  resetReloadGridApl,
} from 'pages/branded-products-apl/controllers/actions';

const { Title } = Typography;

const APLConfirmUpdatePopup = ({
  visible,
  setWarningMissingAPLProductModal,
  missingData,
  fileUpload,
  aplId,
}) => {
  const closeModal = () => {
    setWarningMissingAPLProductModal(false);
  };

  const [submittingAction, setSubmittingAction] = useState(null);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { diffs, urlDownloadDiffs } = missingData || {};

  const columnDefs = [
    {
      headerName: 'Id',
      field: 'id',
      width: 100,
    },
    {
      headerName: 'Name',
      field: 'name',

      width: 200,
    },
    {
      headerName: 'UPC12',
      field: 'upc12',
      width: 100,
    },
    {
      headerName: 'GTIN',
      field: 'gtin',
      width: 100,
    },
    {
      headerName: 'Brand',
      field: 'brand',
      width: 100,
    },
    {
      headerName: 'category',
      field: 'category',
      width: 100,
    },
    {
      headerName: 'contactEmail',
      field: 'contactEmail',
      width: 100,
    },
    {
      headerName: 'ContactName',
      field: 'contactName',
      width: 100,
    },
    {
      headerName: 'ContactPhone',
      field: 'contactPhone',
      width: 100,
    },
    {
      headerName: 'Description',
      field: 'description',
      minWidth: 200,
      flex: 1,
    },
  ];

  const onDownloadMissingProductAPL = () => {
    if (urlDownloadDiffs) saveFile(urlDownloadDiffs);
  };

  const addMissingOrIgnoreProductApl = (AplUpdateOption) => {
    setSubmittingAction(AplUpdateOption);
    api
      .sendUpload({
        api: aplEndpoints.UPLOAD_APL_PRODUCT_LIST,
        payload: {
          file: fileUpload,
        },
        additionalBodyPayload: { AplUpdateOption, AplId: aplId },
        nameFormData: 'TemplateFile',
      })
      .then(({ data }) => {
        if (data?.isSuccess) {
          CustomNotification.success('Upload Product APL successfully!');

          queryClient.invalidateQueries({
            queryKey: ['apl'],
          });

          dispatch(getAplDetail({ aplId }));

          dispatch(toggleReloadGridApl());
          setTimeout(() => {
            dispatch(resetReloadGridApl());
          }, 200);

          dispatch(toggleReloadGrid(true));
          setTimeout(() => {
            dispatch(toggleReloadGrid(false));
          }, 200);
          closeModal();
        } else {
          CustomNotification.warning(data?.message ?? 'Server error');
        }
      })
      .catch(() => {
        CustomNotification.warning('Server error');
      })
      .finally(() => {
        setSubmittingAction(null);
      });
  };

  return (
    <StyledModal
      icon={null}
      visible={visible}
      width={'55%'}
      height={'80%'}
      onCancel={closeModal}
      footer={
        <Row>
          <Col style={{ textAlign: 'left' }}>
            <FormButton
              text='Download Missing Products APL'
              icon={<DownloadOutlined />}
              onClick={onDownloadMissingProductAPL}
            />
          </Col>
          <Col flex={1} style={{ textAlign: 'right' }}>
            <FormSaveButton
              key='continue-apl'
              text='Continue'
              loading={submittingAction === 'Continue'}
              onClick={() => addMissingOrIgnoreProductApl('Continue')}
            />
            <FormAddButton
              key='add-missing-item'
              text='Add Missing Items to New APL'
              loading={submittingAction === 'AddMissing'}
              onClick={() => addMissingOrIgnoreProductApl('AddMissing')}
            />
            <FormCancelButton
              key='cancel-update-apl'
              onClick={() => setWarningMissingAPLProductModal(false)}
            />
          </Col>
        </Row>
      }
    >
      <Title level={5} style={{ padding: 5 }}>
        The following items appear on the existing Master APL, but not within
        the new Master APL.
      </Title>
      <Row style={{ height: '380px' }}>
        <div
          style={{ height: '100%' }}
          className={classnames(
            'inner-col ag-theme-alpine',
            'ag-theme-alpine--with-scroller'
          )}
        >
          <AgGridReact
            columnDefs={columnDefs}
            rowData={diffs}
            headerHeight={32}
            rowHeight={34}
            rowSelection={'single'}
            defaultColDef={{
              sortable: false,
              resizable: true,
              filter: false,
            }}
            overlayNoRowsTemplate='No Data to Show'
          />
        </div>
      </Row>
    </StyledModal>
  );
};

export default APLConfirmUpdatePopup;
